"use client";

import { type MindstringPrototype, type Target } from "@/generated/fastapi";
import { Box } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { memo, useMemo } from "react";
import { useUserChoices } from "@/state/globalStore";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericInteractiveGuidance from "@/shared/ui/display/GenericInteractiveGuidance";
import LoadingState from "@/shared/ui/LoadingState";
import {
  PROFILE_SHAPING_EMPTY_STATES,
  PROFILE_SHAPING_LOADING_STATES,
} from "@/features/profile-shaping/config/actions";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSystemDesign } from "@/shared/system-design";
import useStudioSituationArrangement from "./useStudioSituationArrangement";
import SortableStudioThoughtItem from "./SortableStudioThoughtItem";
import {
  useStudioArrangementDndRegistration,
  useStudioArrangementDropState,
} from "./StudioArrangementDndProvider";
import { getStudioSituationDropId } from "../../utils/situationDropTargets";

type StudioSituationArrangementProps = {
  prototype: MindstringPrototype;
  situationId: string;
  rerankTarget?: Target | null | undefined;
};

type SituationArrangementDropZoneProps = {
  children: ReactNode;
  situationId: string;
};

function SituationArrangementDropZone({
  children,
  situationId,
}: SituationArrangementDropZoneProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: getStudioSituationDropId(situationId),
  });
  const { isDragging, isSource, isTarget } =
    useStudioArrangementDropState(situationId);
  const shouldHighlight = isOver || isTarget;
  const isMoveTarget = shouldHighlight && !isSource;
  let dropZoneBg: string | undefined;
  let dropZoneOutlineColor = "transparent";

  if (shouldHighlight) {
    dropZoneBg = isMoveTarget
      ? "moveon.successLight/30"
      : "moveon.primaryLight/25";
    dropZoneOutlineColor = isMoveTarget ? "moveon.success" : "moveon.primary";
  }

  return (
    <Box
      ref={setNodeRef}
      bg={dropZoneBg}
      opacity={isDragging && !shouldHighlight ? "0.82" : "1"}
      outlineColor={dropZoneOutlineColor}
      outlineOffset="1"
      outlineStyle="solid"
      outlineWidth={shouldHighlight ? "2" : "1"}
      rounded="md"
      minH="8"
      transition="background-color 0.15s ease, opacity 0.15s ease, outline-color 0.15s ease"
    >
      {isMoveTarget ? (
        <Box px="2" pt="2">
          <GenericInteractiveGuidance
            title="Moving resets reflections"
            message="Drop here to anchor the thought to this situation. Existing reflections for that thought will be cleared."
            compactMessage="Drop here to move it. Existing reflections will be cleared."
            icon="warning"
            accentColor="moveon.warning"
            accentBg="moveon.warningLight"
            accentBorderColor="moveon.warningBorder"
          />
        </Box>
      ) : null}
      {children}
    </Box>
  );
}

function StudioSituationArrangementInner({
  prototype,
  situationId,
  rerankTarget,
}: StudioSituationArrangementProps) {
  const { isMobileDevice } = useSystemDesign();
  const { getSelectedThoughts, selectedThoughts } = useUserChoices();
  void selectedThoughts;
  const selectedThoughtsForArrangement = getSelectedThoughts(
    prototype,
    situationId,
  );
  const arrangementScopeKey = [
    prototype,
    situationId,
    rerankTarget?.id ?? rerankTarget?.content ?? "no-rerank-target",
  ].join(":");

  const {
    isLoading,
    sortedThoughts,
    setSortedThoughts,
    setHasManuallyReordered,
  } = useStudioSituationArrangement({
    selectedThoughts: selectedThoughtsForArrangement,
    rerankTarget,
    scopeKey: arrangementScopeKey,
  });

  const registration = useMemo(
    () => ({
      prototype,
      setHasManuallyReordered,
      setSortedThoughts,
      situationId,
      sortedThoughts,
    }),
    [
      prototype,
      setHasManuallyReordered,
      setSortedThoughts,
      situationId,
      sortedThoughts,
    ],
  );

  useStudioArrangementDndRegistration(registration);

  let content: ReactNode;
  if (selectedThoughtsForArrangement.length === 0) {
    content = (
      <GenericEmptyComponent
        title={PROFILE_SHAPING_EMPTY_STATES.orderMode.title}
        message={PROFILE_SHAPING_EMPTY_STATES.orderMode.message}
        variant="inline"
      />
    );
  } else if (isLoading) {
    content = (
      <LoadingState
        loadingMessage={PROFILE_SHAPING_LOADING_STATES.ordering.loadingMessage}
        message={PROFILE_SHAPING_LOADING_STATES.ordering.message}
        variant="inline"
      />
    );
  } else if (sortedThoughts.length === 0) {
    content = (
      <GenericEmptyComponent
        title={PROFILE_SHAPING_EMPTY_STATES.confirmedThoughts.title}
        message={PROFILE_SHAPING_EMPTY_STATES.confirmedThoughts.message}
        variant="inline"
      />
    );
  } else {
    content = (
      <SortableContext
        items={sortedThoughts.map((thought) => thought.objectID)}
        strategy={verticalListSortingStrategy}
      >
        {sortedThoughts.map((thought) => (
          <SortableStudioThoughtItem
            key={thought.objectID}
            thought={thought}
            isMobile={isMobileDevice}
            prototype={prototype}
            selectionSituationId={situationId}
            textMinH="8"
          />
        ))}
      </SortableContext>
    );
  }

  return (
    <Box userSelect="none">
      <SituationArrangementDropZone situationId={situationId}>
        {content}
      </SituationArrangementDropZone>
    </Box>
  );
}

const StudioSituationArrangement = memo(StudioSituationArrangementInner);
export default StudioSituationArrangement;
